import {Image} from '@shopify/hydrogen';
import iconMagnifyingGlass from '../../assets/icons/icon-magnifying-glass.svg';
import {twMerge} from 'tailwind-merge';
import {IconClear} from './Icon';
import {useCallback, useState} from 'react';
import {useEffect} from 'react';
import {DATA_TEST_IDS} from '~/lib/const';

export type SearchInputProps = {
  className?: string;
  type?: string;
  defaultValue?: string;
  refine: (value: string) => void;
} & React.InputHTMLAttributes<HTMLInputElement>;

export function SearchAutocompleteInput({
  className = '',
  type,
  defaultValue,
  onFocus,
  onBlur,
  refine,
  ...props
}: SearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    setValue(defaultValue ?? '');
    defaultValue && refine(defaultValue);
  }, [defaultValue]);

  const onFocusCallback = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const onBlurCallback = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => {
      setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    refine(e.target.value);
  }, []);
  const handleClear = useCallback(() => {
    setValue('');
  }, []);

  return (
    <div className={'w-full bg-gray-200 rounded-[40px] relative h-[40px]'}>
      <input
        data-testid={DATA_TEST_IDS.INPUT('search')}
        type={type}
        {...props}
        className={twMerge(
          'bg-transparent py-0 text-black placeholder:text-gray-400 w-full focus:ring-0 border-0 transition focus:bg-gray/60 px-[50px] font-normal font-sans text-sm leading-[19px] h-[40px]',
          className,
        )}
        value={value}
        onChange={handleChange}
        onFocus={onFocusCallback}
        onBlur={onBlurCallback}
      />
      <button
        data-testid={DATA_TEST_IDS.BUTTON('search-submit')}
        type="submit"
        className={'absolute left-0 bottom-0 top-0 py-2.5 pl-5 pr-2.5'}
      >
        <Image
          loading="lazy"
          src={iconMagnifyingGlass}
          width={'20px'}
          height={'20px'}
          alt={'search'}
        />
      </button>

      {(isFocused || value.length > 0) && (
        <button
          data-testid={DATA_TEST_IDS.BUTTON('search-clear')}
          type="button"
          className={'absolute right-7 top-2.5'}
          onClick={handleClear}
        >
          <IconClear />
        </button>
      )}
    </div>
  );
}
